import React, { useMemo } from 'react';
import CardSection from '~/components/shared/CardSection/CardSection';
import Icon, { SvgSrc } from '~/components/shared/Icon/Icon';
import Slider from '~/components/shared/Slider/Slider';
import { Typography } from '@qred/components-library';
import { formatAmount } from '~/helpers/formatters.helper';
import useTranslate from '~/hooks/useTranslate';
import { ComponentSize } from '~/styles/BaseStyledComponents';
import FlexRow from './FlexRow';

interface Props {
  amount: number;
  maxAllowedAmount: number;
  minAmount: number;
  sliderStep: number;
  onAmountSliderChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const AmountSlider: React.FC<Props> = ({
  amount,
  maxAllowedAmount,
  minAmount,
  sliderStep,
  onAmountSliderChange,
}) => {
  const translate = useTranslate();

  const roundedValue = Math.round(amount);

  const handleStep = useMemo(
    () => (s: number): number => {
      if (s === 1) return s;
      if (maxAllowedAmount - roundedValue < s)
        return handleStep(Math.round(s / 10));
      return s;
    },
    [amount]
  );

  const amountCanBeChanged = minAmount < maxAllowedAmount;

  return (
    <CardSection
      title={
        translate(amountCanBeChanged ? 'ChooseAmount' : 'LoanAmount') as string
      }
      explanation={
        amountCanBeChanged
          ? (translate(
              'LoanConfigurationCard.ChooseAmountExplanation'
            ) as string)
          : undefined
      }
    >
      <FlexRow style={{ marginTop: 6 }}>
        <FlexRow justify="flex-start">
          <Icon
            src={SvgSrc.CommentDollar}
            size={ComponentSize.Small}
            mr={0.4}
          />
          <Typography> {formatAmount(amount)}</Typography>
        </FlexRow>
      </FlexRow>
      {amountCanBeChanged && (
        <Slider
          name="chooseAmount"
          min={minAmount}
          max={maxAllowedAmount}
          step={handleStep(sliderStep)}
          value={roundedValue}
          onChange={onAmountSliderChange}
          dataCy="offer_amount_slider_loan_amount"
          minDescription={`${translate('Lowest')}: ${formatAmount(minAmount)}`}
          maxDescription={`${translate('Highest')}: ${formatAmount(
            maxAllowedAmount
          )}`}
        />
      )}
    </CardSection>
  );
};

export default AmountSlider;
